import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './mediaGateway.api';
import { getSelectedMediaGateway } from './mediaGateway.selectors';
import {
  GET_MEDIA_GATEWAY_LIST,
  GET_MEDIA_GATEWAY,
  CREATE_MEDIA_GATEWAY,
  UPDATE_MEDIA_GATEWAY,
  DELETE_MEDIA_GATEWAY,
  GO_TO_MEDIA_GATEWAY_LIST,
  GO_TO_MEDIA_GATEWAY_EDIT,
  GO_TO_MEDIA_GATEWAY_VIEW,
  GO_TO_MEDIA_GATEWAY_CREATE,
  SELECT_MEDIA_GATEWAY,
  DeleteMediaGatewayThunkAction,
  UpdateMediaGatewayThunkAction,
  CreateMediaGatewayThunkAction,
  SET_MEDIA_GATEWAY_LOADING,
  SetMediaGatewayLoadingAction,
  MediaGatewayActionTypes,
  MediaGateway,
  SelectMediaGatewayAction,
} from './mediaGateway.types';
import { MEDIA_GATEWAY, SYSTEM_CONFIGURATION_ROUTE, SYSTEM } from '../../constants';

export function goToMediaGatewayList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_MEDIA_GATEWAY_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${MEDIA_GATEWAY}`);
  };
}

export function goToMediaGatewayCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_MEDIA_GATEWAY_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${MEDIA_GATEWAY}/create`);
  };
}

export function goToMediaGatewayEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_MEDIA_GATEWAY_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${MEDIA_GATEWAY}/edit`);
  };
}

export function goToMediaGatewayView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_MEDIA_GATEWAY_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${MEDIA_GATEWAY}/view`);
  };
}

export function setMediaGatewayLoadingAction(
  isLoading: boolean,
): SetMediaGatewayLoadingAction {
  return {
    type: SET_MEDIA_GATEWAY_LOADING,
    payload: { isLoading },
  };
}

export function getMediaGatewayList(): MediaGatewayActionTypes {
  return {
    type: GET_MEDIA_GATEWAY_LIST,
    payload: api.getMediaGatewayList(),
  };
}

export function getMediaGateway(
  id: string,
): MediaGatewayActionTypes {
  return {
    type: GET_MEDIA_GATEWAY,
    payload: api.getMediaGateway(id),
  };
}

export function createMediaGateway(
  body: WithAuditDetails<MediaGateway>,
  history: History,
): CreateMediaGatewayThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_MEDIA_GATEWAY,
        payload: api.createMediaGateway(body),
      });

      dispatch(getMediaGatewayList());
      dispatch(goToMediaGatewayList(history));
      showSuccessMessage(i18n.t('mediaGateway.create.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('mediaGateway.create.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function createMediaGatewayFromForm(
  body: WithAuditDetails<MediaGateway>,
): CreateMediaGatewayThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setMediaGatewayLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_MEDIA_GATEWAY,
        payload: api.createMediaGateway(body),
      });

      showSuccessMessage(i18n.t('mediaGateway.create.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('mediaGateway.create.failed', { name: body.data?.name, error: errorMessage }));

    } finally {
      dispatch(setMediaGatewayLoadingAction(false));
      dispatch(getMediaGatewayList());
    }
  };
}

export function updateMediaGateway(
  id: string,
  body: WithAuditDetails<MediaGateway>,
  history: History,
): UpdateMediaGatewayThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_MEDIA_GATEWAY,
        payload: api.updateMediaGateway(id, body),
      });

      dispatch(getMediaGatewayList());
      dispatch(goToMediaGatewayList(history));
      showSuccessMessage(i18n.t('mediaGateway.update.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('mediaGateway.update.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function deleteMediaGateway(
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteMediaGatewayThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedMediaGateway] = getSelectedMediaGateway(getState());

    try {
      await dispatch({
        type: DELETE_MEDIA_GATEWAY,
        payload: api.deleteMediaGateway(id, body),
      });

      showSuccessMessage(i18n.t('mediaGateway.delete.success', { name: selectedMediaGateway?.name }), 5000);
      if (history != null) {
        dispatch(goToMediaGatewayList(history));
      }
    } catch (e) {
      showError(i18n.t('mediaGateway.delete.failed', { name: selectedMediaGateway?.name }));

    } finally {
      dispatch(getMediaGatewayList());
    }
  };
}

export function selectMediaGateway(
  mediaGateway: MediaGateway[],
): SelectMediaGatewayAction {
  return {
    type: SELECT_MEDIA_GATEWAY,
    payload: mediaGateway,
  };
}
